import type { IntlShape } from 'react-intl';

import type {
  UserFacingAuditChange,
  UserFacingAuditChangesPayload,
  UserFacingAuditDateRange,
  UserFacingAuditValue,
} from '@src/models/interfaces/AuditLog/AuditLogInterface';
import { AUDIT_DATE_FORMAT, DateUtil } from '@src/packages/utils/dates';

const FIELD_MESSAGE_PREFIX = 'auditLogs.fields.';
const REDACTED_MESSAGE_ID = 'auditLogs.values.redacted';
const EMPTY_MESSAGE_ID = 'auditLogs.values.empty';
const COUNT_MESSAGE_ID = 'auditLogs.values.count';
const TRUE_MESSAGE_ID = 'auditLogs.values.true';
const FALSE_MESSAGE_ID = 'auditLogs.values.false';
const CHANGE_SUMMARY_ID = 'auditLogs.changeSummary';
const NO_DETAILS_ID = 'auditLogs.noMeaningfulChanges';
const RANGE_SEPARATOR_ID = 'auditLogs.values.rangeSeparator';

const formatMessageOr = (
  intl: IntlShape,
  messageId: string,
  fallback: string,
  values?: Record<string, string | number>,
): string => {
  if (!intl.messages[messageId]) {
    return fallback;
  }
  return intl.formatMessage({ id: messageId }, values);
};

export const getAuditFieldLabel = (intl: IntlShape, field: string): string => {
  const messageId = `${FIELD_MESSAGE_PREFIX}${field}`;
  return formatMessageOr(intl, messageId, field.replace(/_/g, ' '));
};

export const isUserFacingAuditDateRange = (
  value: UserFacingAuditValue | undefined,
): value is UserFacingAuditDateRange => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  return 'start' in value && 'end' in value;
};

const formatDateTimeInstant = (
  intl: IntlShape,
  value: string | null,
  timeZone?: string | null,
): string => {
  if (!value) {
    return formatMessageOr(intl, EMPTY_MESSAGE_ID, '—');
  }
  const millis = DateUtil.getTimestampMs(value);
  if (millis === null) {
    return value;
  }
  return DateUtil.toDateString(millis, AUDIT_DATE_FORMAT, timeZone ?? null) || value;
};

const formatDateRange = (
  intl: IntlShape,
  range: UserFacingAuditDateRange,
  timeZone?: string | null,
): string => {
  const separator = formatMessageOr(intl, RANGE_SEPARATOR_ID, '→');
  return `${formatDateTimeInstant(intl, range.start, timeZone)} ${separator} ${formatDateTimeInstant(
    intl,
    range.end,
    timeZone,
  )}`;
};

const formatDisplayValue = (
  intl: IntlShape,
  change: UserFacingAuditChange,
  value: UserFacingAuditValue | undefined,
  timeZone?: string | null,
): string => {
  if (change.kind === 'redacted') {
    return formatMessageOr(intl, REDACTED_MESSAGE_ID, 'Updated (hidden)');
  }

  if (value === undefined || value === null || value === '') {
    return formatMessageOr(intl, EMPTY_MESSAGE_ID, '—');
  }

  if (change.kind === 'boolean') {
    return value
      ? formatMessageOr(intl, TRUE_MESSAGE_ID, 'Yes')
      : formatMessageOr(intl, FALSE_MESSAGE_ID, 'No');
  }

  if (change.kind === 'datetime') {
    if (isUserFacingAuditDateRange(value)) {
      return formatDateRange(intl, value, timeZone);
    }
    if (typeof value === 'string') {
      return formatDateTimeInstant(intl, value, timeZone);
    }
  }

  if ((change.kind === 'count' || change.kind === 'list_summary') && typeof value === 'number') {
    return formatMessageOr(intl, COUNT_MESSAGE_ID, `${value}`, { count: value });
  }

  if (typeof value === 'object') {
    return formatMessageOr(intl, EMPTY_MESSAGE_ID, '—');
  }

  return String(value);
};

export const formatAuditChangeValue = (
  intl: IntlShape,
  change: UserFacingAuditChange,
  side: 'old' | 'new',
  timeZone?: string | null,
): string =>
  formatDisplayValue(intl, change, side === 'old' ? change.oldValue : change.newValue, timeZone);

export const getAuditChangedFieldLabels = (
  intl: IntlShape,
  userFacingChanges?: UserFacingAuditChangesPayload | null,
): string => {
  const changes = userFacingChanges?.changes ?? [];
  if (!changes.length) {
    return '';
  }

  const labels = changes.map(change => getAuditFieldLabel(intl, change.field));
  const uniqueLabels = Array.from(new Set(labels));
  const fields = uniqueLabels.slice(0, 4).join(', ');
  const extra = uniqueLabels.length > 4 ? ` (+${uniqueLabels.length - 4})` : '';
  return fields + extra;
};

export const getAuditChangesSummary = (
  intl: IntlShape,
  userFacingChanges?: UserFacingAuditChangesPayload | null,
): string => {
  const fields = getAuditChangedFieldLabels(intl, userFacingChanges);
  if (!fields) {
    return formatMessageOr(intl, NO_DETAILS_ID, '');
  }

  return formatMessageOr(intl, CHANGE_SUMMARY_ID, fields, { fields });
};

export const getAuditChangeRows = (
  userFacingChanges?: UserFacingAuditChangesPayload | null,
): UserFacingAuditChange[] => userFacingChanges?.changes ?? [];
